import React from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Link } from 'gatsby'

interface Authors {
  data: {
    Person_Company: {
      data: {
        Company_Name: string
      }
    }
    Person_First: string
    Person_Job_Title: string
    Person_Last: string
    Profile_Picture_Url_40: string
    Person_Url_Slug: string
  }
  id: string
}
export interface StandardArticleCardProps {
  post: {
    data: {
      Author: Authors[]
      Type: string
      Ghost_Slug: string
      Release_Date: string
      excerpt: string
      feature_image: string
      feature_image_sharp: any
      title: string
    }
    id: string
  }
  authors: Authors[]
  className?: {
    main: string
    image: string
  }
}

const Authors = ({ authors, formattedDate }: { authors: Authors[]; formattedDate: string }) => {
  if (!authors) return <></>
  return (
    <div>
      {authors.map((author) => {
        return (
          <Link
            to={'/profile/' + author.data.Person_Url_Slug}
            key={'author' + author.id}
            className="flex items-center mt-4"
          >
            <div className="text-xs text-gray-500">
              <span className="font-bold">{formattedDate}</span> · {author.data.Person_First}{' '}
              {author.data.Person_Last}, {author.data.Person_Job_Title}
              {author.data.Person_Company && author.data.Person_Company[0] && (
                <span className="italic">, {author.data.Person_Company[0].data.Company_Name}</span>
              )}
            </div>
          </Link>
        )
      })}
    </div>
  )
}

export const StandardArticleCard = ({ post, authors, className }: StandardArticleCardProps) => {
  const headerImage = post?.data?.feature_image_sharp?.childImageSharp?.gatsbyImageData
  const formattedDate = new Date(post.data?.Release_Date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  return (
    <div className={className?.main ? className.main : 'w-full m-auto'}>
      <div className="my-8 pb-12 md:flex md:flex-row-reverse border-b border-gray-200 w-full">
        <div>
          {headerImage && (
            <Link to={'/blog/' + post.data.Ghost_Slug} className="md:ml-8 w-full">
              <GatsbyImage
                image={headerImage}
                className={`${className?.image ? className.image : 'h-48 object-cover md:ml-8'} w-full `}
                alt={post.data.title}
              />
            </Link>
          )}
        </div>
        <div className="flex flex-col justify-between pt-8 md:pt-0">
          <Link to={'/blog/' + post.data.Ghost_Slug}>
            <p className="text-xl font-bold text-gray-900">{post.data.title}</p>
            <p className="mt-4 text-base text-gray-500 line-clamp-3">{post.data.excerpt}</p>
          </Link>
          <div className="mt-4">
            <Authors authors={authors} formattedDate={formattedDate} />
          </div>
        </div>
      </div>
    </div>
  )
}

export const HeroArticleCard = ({ post, authors, className }: StandardArticleCardProps) => {
  const headerImage = post.data.feature_image_sharp?.childImageSharp?.gatsbyImageData
  const formattedDate = new Date(post.data?.Release_Date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  return (
    <div className="w-full pb-8 border-b border-gray-200">
      <div>
        {headerImage && (
          <Link to={'/blog/' + post.data.Ghost_Slug}>
            <GatsbyImage
              image={headerImage}
              className={className?.image ? className.image : 'h-48 w-full object-cover'}
              alt={post.data.title}
            />
          </Link>
        )}
      </div>
      <div className="w-full flex-1 bg-white py-3 flex flex-col justify-between">
        <div className="flex-1">
          <Link to={'/blog/' + post.data.Ghost_Slug} className="block mt-2">
            <p className="text-xl font-semibold text-gray-900">{post.data.title}</p>
            <p className="mt-3 text-base text-gray-500 line-clamp-3">{post.data.excerpt}</p>
          </Link>
        </div>
        <div className="mt-2 flex items-center">
          <Authors authors={authors} formattedDate={formattedDate} />
        </div>
      </div>
    </div>
  )
}

export const SquareArticleCard = ({ post, authors }) => {
  const headerImage = post.data.feature_image_sharp?.childImageSharp?.gatsbyImageData
  return (
    <>
      <div className="flex-shrink-0">
        {headerImage && (
          <Link to={'/blog/' + post.data.Ghost_Slug}>
            <GatsbyImage image={headerImage} className="h-48 w-full object-cover" alt={post.data.title} />
          </Link>
        )}
      </div>
      <div className="flex-1 bg-white p-6 flex flex-col justify-between">
        <div className="flex-1">
          {/* <p className='text-sm font-medium text-indigo-600'>
          <Link to={'/blog?category=' + post.data.category} className='hover:underline'>
            CREATE AGREED CATEGORIES
          </Link>
        </p> */}
          <Link to={'/blog/' + post.data.Ghost_Slug} className="block mt-2">
            <p className="text-xl font-semibold text-gray-900">{post.data.title}</p>
            <p className="mt-3 text-base text-gray-500 line-clamp-3">{post.data.excerpt}</p>
          </Link>
        </div>
        <div className="mt-6 flex items-center">
          {authors && (
            <div className="flex flex-wrap">
              {authors.map((author) => {
                const authorImage = author.data.Profile_Picture_Url_40
                return (
                  <Link
                    to={'/profile/' + author.data.Person_Url_Slug}
                    key={author.id}
                    className="flex items-center mt-4 mr-4"
                  >
                    {authorImage && (
                      // <GatsbyImage
                      //   image={authorImage}
                      //   alt={author.data.Person_First}
                      //   className="w-10 h-10 rounded-full mr-2"
                      // />
                      <img
                        src={authorImage}
                        alt={author.data.Person_First}
                        className="w-10 h-10 rounded-full mr-2"
                        loading="lazy"
                      />
                    )}
                    <div>
                      <p className="text-sm text-gray-700 line-clamp-1">
                        {author.data.Person_First} {author.data.Person_Last}
                      </p>
                      <p className="text-xs text-gray-700 line-clamp-1">
                        {author.data.Person_Job_Title}
                        {author.data.Person_Company && author.data.Person_Company[0] && (
                          <>
                            <span aria-hidden="true" className="mx-1">
                              &middot;
                            </span>
                            {author.data.Person_Company[0].data.Company_Name}
                          </>
                        )}
                      </p>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
